import { useCallback, useEffect, useRef, useState } from "react";
import { audible, stripOf } from "@/lib/bgvApi";
import { absUrl } from "@/lib/voiceSongsApi";

const dbToGain = (db) => 10 ** (db / 20);

function chain(ctx, el, dry, wet) {
  const src = ctx.createMediaElementSource(el);
  const gain = ctx.createGain();
  const pan = ctx.createStereoPanner();
  const send = ctx.createGain();
  src.connect(gain).connect(pan).connect(dry);
  pan.connect(send).connect(wet);
  return { gain, pan, send };
}

function apply(nodes, settings, names) {
  const live = new Set(audible(settings, names));
  Object.entries(nodes).forEach(([n, x]) => {
    const s = stripOf(settings, n);
    if (!s) return;
    x.gain.gain.value = live.has(n) ? dbToGain(s.db) : 0;
    x.pan.pan.value = s.pan;
    x.send.gain.value = s.reverb / 100;
  });
}

/** Live preview of every track (lead, singers, choir, music) on one clock, with the same level / pan / reverb law as the saved mix. */
export default function useBgvPreview(tracks, settings, reverb) {
  const ctxRef = useRef(null);
  const els = useRef({});
  const nodes = useRef({});
  const [state, setState] = useState("loading");
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const live = useRef(settings);
  live.current = settings;
  const names = tracks.map((t) => t.name);
  const key = tracks.map((t) => t.url).join("|");

  useEffect(() => {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    ctxRef.current = ctx;
    const dry = ctx.createGain();
    const wet = ctx.createGain();
    const limiter = ctx.createDynamicsCompressor();
    limiter.knee.value = 0; limiter.ratio.value = 20; limiter.attack.value = 0.003; limiter.release.value = 0.06; limiter.threshold.value = -1;
    dry.connect(limiter).connect(ctx.destination);
    tracks.forEach((t) => {
      const a = new Audio();
      a.preload = "auto";
      a.src = absUrl(t.url);
      els.current[t.name] = a;
      nodes.current[t.name] = chain(ctx, a, dry, wet);
    });
    apply(nodes.current, live.current, names);
    fetch(absUrl(reverb.url)).then((r) => r.arrayBuffer()).then((b) => ctx.decodeAudioData(b)).then((buf) => {
      if (ctx.state === "closed") return;
      const conv = Object.assign(ctx.createConvolver(), { normalize: false, buffer: buf });
      const scale = ctx.createGain();
      scale.gain.value = reverb.scale;
      wet.connect(conv).connect(scale).connect(dry);
    }).catch(() => {});
    const lead = els.current[tracks[0]?.name];
    if (!lead) return undefined;
    setState("loading");
    const onMeta = () => { if (Number.isFinite(lead.duration)) setDuration(lead.duration); setState("ready"); };
    const onEnd = () => { Object.values(els.current).forEach((a) => a.pause()); setPlaying(false); };
    lead.addEventListener("loadedmetadata", onMeta);
    lead.addEventListener("ended", onEnd);
    lead.addEventListener("error", () => setState("error"));
    return () => {
      Object.values(els.current).forEach((a) => { a.pause(); a.removeAttribute("src"); a.load(); });
      els.current = {};
      nodes.current = {};
      setPlaying(false);
      ctx.close();
    };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { apply(nodes.current, settings, names); }, [settings, key]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!playing) return undefined;
    const t = setInterval(() => {
      const all = names.map((n) => els.current[n]).filter(Boolean);
      const now = all[0]?.currentTime || 0;
      setTime(now);
      all.slice(1).forEach((a) => { if (Math.abs(a.currentTime - now) > 0.08) a.currentTime = now; });
    }, 200);
    return () => clearInterval(t);
  }, [playing, key]); // eslint-disable-line react-hooks/exhaustive-deps

  const play = useCallback(async () => {
    const all = Object.values(els.current);
    const at = all[0]?.currentTime || 0;
    ctxRef.current?.resume();
    all.forEach((a) => { a.currentTime = at; });
    try { await Promise.all(all.map((a) => a.play())); setPlaying(true); }
    catch { all.forEach((a) => a.pause()); setPlaying(false); }
  }, []);
  const pause = useCallback(() => { Object.values(els.current).forEach((a) => a.pause()); setPlaying(false); }, []);
  const seek = useCallback((t) => { Object.values(els.current).forEach((a) => { a.currentTime = t; }); setTime(t); }, []);
  return { state, playing, time, duration, play, pause, seek };
}
